import React, { useCallback, useEffect, useRef, useState } from "react";
import api from "@/lib/api";
import { Activity, CheckCircle2, AlertTriangle, XCircle, RefreshCw, ClipboardCopy, Check, Gauge } from "lucide-react";

const STATUS_STYLE = {
    ok: { icon: CheckCircle2, color: "health-status-ok", border: "border-emerald-500/30", bg: "bg-emerald-500/5", label: "OK" },
    warning: { icon: AlertTriangle, color: "health-status-warning", border: "border-amber-500/40", bg: "bg-amber-500/5", label: "Attenzione" },
    error: { icon: XCircle, color: "health-status-error", border: "border-red-500/50", bg: "bg-red-900/10", label: "Errore" },
};

const AUTO_REFRESH_MS = 45000;

export default function AdminHealthCenter() {
    const [data, setData] = useState(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState("");
    const [copiedId, setCopiedId] = useState(null);
    const intervalRef = useRef(null);

    const load = useCallback(async (silent = false) => {
        if (!silent) setLoading(true);
        try {
            const r = await api.get("/admin/health/status");
            setData(r.data);
            setError("");
        } catch (e) {
            setError("Impossibile eseguire la scansione di diagnostica. Riprova.");
        } finally {
            setLoading(false);
        }
    }, []);

    useEffect(() => {
        load();
        intervalRef.current = setInterval(() => {
            if (!document.hidden) load(true);
        }, AUTO_REFRESH_MS);
        return () => clearInterval(intervalRef.current);
    }, [load]);

    const copyPrompt = async (id, prompt) => {
        try {
            await navigator.clipboard.writeText(prompt);
            setCopiedId(id);
            setTimeout(() => setCopiedId(null), 2500);
        } catch (e) {
            setError("Impossibile copiare automaticamente: seleziona e copia manualmente il testo del prompt.");
        }
    };

    const overall = data?.overall_status || "ok";
    const OverallIcon = STATUS_STYLE[overall].icon;

    return (
        <div data-testid="admin-health-center">
            <div className="mb-6 flex flex-wrap items-start justify-between gap-4">
                <div>
                    <span className="label-eyebrow text-gold">Super-Admin</span>
                    <h2 className="font-serif text-2xl text-ink-50 mt-2 flex items-center gap-2">
                        <Activity size={20} className="text-gold" /> Stato e Diagnosi del Sistema
                    </h2>
                    <p className="text-ink-400 text-sm mt-1">
                        Scansione leggera delle aree critiche della piattaforma, con aggiornamento automatico ogni {AUTO_REFRESH_MS / 1000}s.
                    </p>
                </div>
                <button
                    data-testid="health-refresh-btn"
                    onClick={() => load(false)}
                    disabled={loading}
                    className="cta-outline px-4 py-2 text-xs flex items-center gap-1.5 disabled:opacity-50"
                >
                    <RefreshCw size={13} className={loading ? "animate-spin" : ""} /> Aggiorna adesso
                </button>
            </div>

            {error && <div className="mb-4 p-3 border border-red-500/40 bg-red-900/20 text-red-200 text-xs" data-testid="health-load-error">{error}</div>}

            {!data ? (
                <div className="text-ink-400 text-sm py-12 text-center">Scansione in corso…</div>
            ) : (
                <>
                    <div
                        data-testid="health-overall-status"
                        className={`flex items-center gap-3 border p-4 mb-6 ${STATUS_STYLE[overall].border} ${STATUS_STYLE[overall].bg}`}
                    >
                        <OverallIcon size={20} className={STATUS_STYLE[overall].color} />
                        <div>
                            <div className={`text-sm font-semibold uppercase tracking-wider ${STATUS_STYLE[overall].color}`}>
                                Stato generale: {STATUS_STYLE[overall].label}
                            </div>
                            <div className="text-[11px] text-ink-500 mt-0.5">
                                Ultimo controllo: {new Date(data.generated_at).toLocaleTimeString("it-IT")}
                            </div>
                        </div>
                    </div>

                    <div className="space-y-4">
                        {data.checks.map((c) => (
                            <CheckCard key={c.id} check={c} copiedId={copiedId} onCopy={copyPrompt} />
                        ))}
                    </div>
                </>
            )}
        </div>
    );
}

function CheckCard({ check, copiedId, onCopy }) {
    const s = STATUS_STYLE[check.status];
    const Icon = s.icon;
    return (
        <div data-testid={`health-check-${check.id}`} className={`border p-5 ${check.status === "ok" ? "border-white/10" : s.border}`}>
            <div className="flex items-center justify-between gap-4 flex-wrap">
                <div className="flex items-center gap-3">
                    <Icon size={17} className={s.color} />
                    <div>
                        <div className="text-sm text-ink-50 font-medium">{check.label}</div>
                        <div className="text-xs text-ink-400 mt-0.5">{check.summary}</div>
                    </div>
                </div>
                <div className="flex items-center gap-3">
                    {check.latency_ms !== undefined && (
                        <span className="text-[10px] text-ink-500 flex items-center gap-1"><Gauge size={11} /> {check.latency_ms} ms</span>
                    )}
                    <span className={`text-[10px] uppercase tracking-wider px-2 py-1 border ${s.border} ${s.color}`}>{s.label}</span>
                </div>
            </div>

            {check.detail && (
                <div className={`mt-4 p-4 border ${s.border} ${s.bg}`} data-testid={`health-detail-${check.id}`}>
                    <div className="space-y-2 text-xs text-ink-100 leading-relaxed">
                        <div><span className="uppercase tracking-wider text-ink-400">Cosa è successo:</span> {check.detail.cosa_e_successo}</div>
                        <div><span className="uppercase tracking-wider text-ink-400">Dov'è il problema:</span> {check.detail.dove}</div>
                        <div><span className="uppercase tracking-wider text-ink-400">Soluzione suggerita:</span> {check.detail.soluzione}</div>
                    </div>
                    <button
                        data-testid={`health-copy-prompt-${check.id}`}
                        onClick={() => onCopy(check.id, check.detail.repair_prompt)}
                        className={`mt-4 px-4 py-2 text-xs border flex items-center gap-2 ${s.border} ${s.color} hover:bg-white/5`}
                    >
                        {copiedId === check.id ? <><Check size={13} /> Copiato negli appunti</> : <><ClipboardCopy size={13} /> Copia Prompt di Riparazione</>}
                    </button>
                </div>
            )}
        </div>
    );
}
